import React, {
  forwardRef,
  Fragment,
  ReactElement,
  Ref,
  useImperativeHandle,
  useRef,
} from 'react';
import { Popover, Transition } from '@headlessui/react';
import { DesktopComputerIcon, DocumentAddIcon } from '@heroicons/react/outline';
import { ChevronDownIcon, XCircleIcon } from '@heroicons/react/solid';
import { FileToUpload } from 'components/file/file-upload-button';
import { ProgressBar } from 'components/progress-bar';

interface Props {
  filesToUpload: FileToUpload[];
  removeUpload: (file: FileToUpload) => void;
  setUploadDialogOpen: (open: boolean) => void;
}

export interface FileUploadsDropdownHandle {
  open: () => void;
}

export const FileUploadsDropdown = forwardRef(
  (props: Props, ref: Ref<FileUploadsDropdownHandle>): ReactElement => {
    const pendingUploads = props.filesToUpload?.filter(
      (f) => f.status === 'pending' || f.status === 'uploading'
    );

    const buttonRef = useRef<HTMLButtonElement>();

    useImperativeHandle(ref, () => ({
      open() {
        buttonRef.current.click();
      },
    }));

    return (
      <>
        <Popover className="relative">
          {() => (
            <>
              <Popover.Button
                className="disabled:pointer-events-auto disabled:cursor-not-allowed inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-70 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-40"
                ref={buttonRef}
              >
                {pendingUploads?.length > 0 ? (
                  <span className="inline-flex justify-center items-center -ml-1 mr-2 h-5 w-5 text-xs font-semibold text-black bg-blue-200 rounded-full">
                    {pendingUploads.length}
                  </span>
                ) : (
                  <DesktopComputerIcon
                    className="-ml-1 mr-2 h-5 w-5 text-white"
                    aria-hidden="true"
                  />
                )}
                <span>Upload files</span>
                <ChevronDownIcon className="ml-2 h-5 w-5" aria-hidden="true" />
              </Popover.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel className="absolute z-20 -ml-4 mt-3 transform px-2 w-screen max-w-sm sm:px-0 lg:ml-0 lg:left-1/2 lg:-translate-x-1/2">
                  <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
                    <div className="relative grid gap-3 bg-white px-5 py-6 sm:p-8">
                      <div>
                        <button
                          type="button"
                          className="peer disabled:pointer-events-auto disabled:cursor-not-allowed inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-40"
                          onClick={() => props.setUploadDialogOpen(true)}
                        >
                          <DocumentAddIcon
                            className="-ml-1 mr-2 h-5 w-5 text-blue-700"
                            aria-hidden="true"
                          />
                          Add files
                        </button>
                      </div>
                      {props.filesToUpload.map((f) => (
                        <FileItem
                          key={f.id}
                          fileToUpload={f}
                          onRemoveClick={() => props.removeUpload(f)}
                        />
                      ))}
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
      </>
    );
  }
);

function FileItem({
  fileToUpload,
  onRemoveClick,
}: {
  fileToUpload: FileToUpload;
  onRemoveClick: () => void;
}) {
  return (
    <div className="w-full">
      <div className="w-full inline-flex justify-center items-center gap-1">
        <p className="flex-1 break-all">{fileToUpload.file.name}</p>
        <button
          type="button"
          className="font-bold text-red-500 hover:text-red-700"
          onClick={() => onRemoveClick()}
        >
          <XCircleIcon className="mr-2 h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div className="mt-1">
        {fileToUpload.status === 'error' ? (
          <p className="text-red-500">Error uploading, please try again</p>
        ) : (
          <ProgressBar percentage={fileToUpload.uploadPercentage || 0} />
        )}
      </div>
    </div>
  );
}

FileUploadsDropdown.displayName = 'FileUploadsDropdown';
